/**
 * SKU表单 - 基础字段（舒适布局版）
 * 职责: SKU名称、ASIN、品牌、类目、产品标识码
 */

import React from 'react';
import { Form, Input, Select, Radio, AutoComplete, Row, Col, InputNumber, Divider } from 'antd';
import type { BrandSimple } from '../../services/brandService';
import { AMAZON_CATEGORIES } from '../../constants/amazonCategories';

const { Option } = Select;
const { TextArea } = Input;

interface SKUFormBasicFieldsProps {
    brands: BrandSimple[];
    brandMode: 'select' | 'custom';
    onBrandModeChange: (mode: 'select' | 'custom') => void;
    isEditing?: boolean;
}

export const SKUFormBasicFields: React.FC<SKUFormBasicFieldsProps> = ({
    brands,
    brandMode,
    onBrandModeChange,
    isEditing = false,
}) => {
    return (
        <div style={{ marginTop: 12 }}>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        name="sku_code"
                        label="SKU编码"
                        initialValue={!isEditing ? '' : undefined}
                        rules={[{ required: true, message: '请输入SKU编码' }]}
                        tooltip={isEditing ? "修改SKU编码可能会影响关联数据，请谨慎操作" : "建议使用唯一的编码标识，如: IS30878B"}
                        style={{ marginBottom: 16 }}
                    >
                        <Input placeholder="唯一标识" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="name"
                        label="SKU名称 (品名)"
                        rules={[{ required: true, message: '请输入SKU名称' }]}
                        style={{ marginBottom: 16 }}
                    >
                        <Input placeholder="产品名称" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="status"
                        label="状态"
                        initialValue="active"
                        style={{ marginBottom: 16 }}
                    >
                        <Select placeholder="SKU状态">
                            <Option value="developing">开发中</Option>
                            <Option value="sample">打样中</Option>
                            <Option value="active">在售</Option>
                            <Option value="inactive">停售</Option>
                            <Option value="clearance">清仓</Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="name_cn" label="中文名称" style={{ marginBottom: 16 }}>
                        <Input placeholder="中文名称" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="name_en" label="英文名称" style={{ marginBottom: 16 }}>
                        <Input placeholder="英文名称" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="purchase_cost_cny"
                        label="采购成本(CNY)"
                        style={{ marginBottom: 16 }}
                    >
                        <InputNumber
                            placeholder="采购单价"
                            prefix="¥"
                            style={{ width: '100%' }}
                            precision={2}
                            min={0}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        name="category"
                        label="亚马逊类目"
                        style={{ marginBottom: 16 }}
                    >
                        <AutoComplete
                            options={AMAZON_CATEGORIES.map(cat => ({ value: cat }))}
                            placeholder="选择或输入类目"
                            filterOption={(inputValue, option) =>
                                option!.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                            }
                            allowClear
                        />
                    </Form.Item>
                </Col>
                <Col span={16}>
                    <Form.Item label="品牌" style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Radio.Group
                                value={brandMode}
                                onChange={(e) => onBrandModeChange(e.target.value)}
                                optionType="button"
                                buttonStyle="solid"
                                size="middle"
                            >
                                <Radio.Button value="select">选择</Radio.Button>
                                <Radio.Button value="custom">自定义</Radio.Button>
                            </Radio.Group>
                            <div style={{ flex: 1 }}>
                                {brandMode === 'select' ? (
                                    <Form.Item name="brand_obj" noStyle>
                                        <Select placeholder="选择品牌" allowClear showSearch optionFilterProp="children">
                                            {brands.map((brand) => (
                                                <Option key={brand.id} value={brand.id}>
                                                    {brand.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                ) : (
                                    <Form.Item name="brand" noStyle>
                                        <Input placeholder="输入品牌名称" />
                                    </Form.Item>
                                )}
                            </div>
                        </div>
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item name="description" label="产品描述" style={{ marginBottom: 16 }}>
                <TextArea rows={4} placeholder="请输入详细的产品描述..." showCount maxLength={2000} />
            </Form.Item>
        </div>
    );
};
